/**
 * Reine Rechenlogik für Rechnungen (framework-frei).
 */

import { computeQuoteTotals, type CalcItem, type QuoteTotals } from './quote'

export type InvoiceKind = 'standalone' | 'advance' | 'partial' | 'final' | 'credit_note'
export type InvoiceStatus =
  | 'draft'
  | 'sent'
  | 'partially_paid'
  | 'paid'
  | 'overdue'
  | 'cancelled'

/**
 * Rechnungssummen: wie beim Angebot, aber ohne „optional/alternative"-Ausschluss
 * — jede Position zählt. (Bei der Konvertierung werden optionale/nicht gewählte
 * Positionen bereits herausgefiltert.)
 */
export function computeInvoiceTotals(
  items: CalcItem[],
  opts: { taxScheme: 'regel' | 'kleinunternehmer'; taxRatePercent: number; reverseChargeBau: boolean },
  precomputedLabels?: Record<string, string>,
): QuoteTotals {
  // Positionen zählen alle (optionale/nicht gewählte werden bei der Konvertierung
  // bereits herausgefiltert); Rabatt-Zeilen bleiben unverändert.
  const asMandatory = items.map((i) =>
    i.kind === 'position' ? { ...i, mode: 'mandatory' as const, isChosenAlternative: false } : i,
  )
  return computeQuoteTotals(asMandatory, opts, precomputedLabels)
}

/**
 * Für Umsatz-/Steuer-Auswertungen (UVA, Dashboard, Jahresübersicht): eine Schlussrechnung
 * (`kind === 'final'`) enthält immer die vollen Positionen des Auftrags — richtig so für die
 * PDF-Anzeige ("abzüglich bereits gestellter Anzahlung"), aber falsch für Umsatz-/USt-Summen,
 * weil die Anzahlungs-/Teilrechnungen ihren Betrag bereits eigenständig gemeldet haben. Ohne
 * Abzug würde derselbe Umsatz doppelt gezählt. `deductionNetCents`/`deductionTaxCents` (Summe der
 * bereits gestellten Anzahlungen) zieht genau den schon gemeldeten Anteil wieder ab.
 *
 * Eine Gutschrift (`kind === 'credit_note'`) speichert ihre Positionen bewusst als positive Werte
 * (normale Eingabe, normale PDF-Darstellung) — der Vorzeichenwechsel für Umsatz-/USt-Zwecke passiert
 * ausschließlich hier an dieser einen Stelle, statt negative Beträge durchs ganze System zu ziehen.
 */
export function invoiceRevenueNetCents(inv: {
  kind: string
  netCents: number
  deductionNetCents: number
}): number {
  if (inv.kind === 'credit_note') return -inv.netCents
  return inv.kind === 'final' ? inv.netCents - inv.deductionNetCents : inv.netCents
}
export function invoiceRevenueTaxCents(inv: {
  kind: string
  taxCents: number
  deductionTaxCents: number
}): number {
  if (inv.kind === 'credit_note') return -inv.taxCents
  return inv.kind === 'final' ? inv.taxCents - inv.deductionTaxCents : inv.taxCents
}

export interface DeductionInput {
  netCents: number
  taxCents: number
  grossCents: number
}

/** Summiert bereits gestellte Anzahlungs-/Teilrechnungen für eine Schlussrechnung. */
export function sumDeductions(prior: DeductionInput[]): DeductionInput {
  return prior.reduce(
    (acc, p) => ({
      netCents: acc.netCents + p.netCents,
      taxCents: acc.taxCents + p.taxCents,
      grossCents: acc.grossCents + p.grossCents,
    }),
    { netCents: 0, taxCents: 0, grossCents: 0 },
  )
}

/**
 * Leitet den Rechnungsstatus aus Zahlungsstand und Fälligkeit ab.
 * `cancelled` bleibt erhalten; ohne Versand bleibt es beim aktuellen Status.
 */
export function deriveInvoiceStatus(input: {
  currentStatus: InvoiceStatus
  payableGrossCents: number
  paidCents: number
  dueDate: Date
  sentAt: Date | null
  today?: Date
}): InvoiceStatus {
  if (input.currentStatus === 'cancelled') return 'cancelled'
  if (!input.sentAt) return input.currentStatus === 'draft' ? 'draft' : input.currentStatus

  const ref = input.today ?? new Date()
  const startOfToday = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate()).getTime()
  // Nichts (mehr) zu zahlen — z. B. eine Schlussrechnung, die durch die Anzahlung bereits
  // vollständig gedeckt ist (payableGrossCents = 0). Ohne diesen Fall bliebe sie für immer auf
  // "gesendet"/"überfällig" stehen, weil kein zusätzlicher Zahlungseingang mehr erwartet wird.
  if (input.payableGrossCents <= 0) return 'paid'
  if (input.paidCents >= input.payableGrossCents) return 'paid'
  if (input.paidCents > 0) return 'partially_paid'
  if (input.dueDate.getTime() < startOfToday) return 'overdue'
  return 'sent'
}

export interface ScheduleLineInput {
  basis: 'percent' | 'fixed'
  value: number
}

/** Rechnet Zahlungsplan-Zeilen in konkrete Cent-Beträge um (Basis = Bruttobetrag). */
export function resolveSchedule(lines: ScheduleLineInput[], baseGrossCents: number): number[] {
  return lines.map((l) =>
    l.basis === 'percent' ? Math.round((baseGrossCents * l.value) / 100) : Math.round(l.value),
  )
}

/** Verbleibender Betrag bei Skontoabzug (Skonto rechnet vom Bruttobetrag). */
export function skontoAmountCents(grossCents: number, skontoPercent: number): number {
  return Math.round(grossCents * (1 - (skontoPercent || 0) / 100))
}
